// =============================================================================
// allocationImportExportService.js - Import/Export Service for Allocations
// =============================================================================
// Handles exporting allocations to CSV/JSON and importing from CSV/JSON
// 
// UPDATED: Now includes facilityId in all imports/exports
// =============================================================================

import { collection, query, where, getDocs, writeBatch, doc } from 'firebase/firestore';
import { db } from '../../utils/firebase';
import { buildFirestorePath } from '../../utils/settings/settingsConfig';
import allocationService from './allocationService';

// ==================== PITCH NAMING HELPERS ====================

/**
 * Normalize pitch ID to consistent format: pitch-{number}
 */
const normalizePitchId = (pitchId) => {
  if (!pitchId) return '';
  const id = String(pitchId);
  if (id.startsWith('pitch-')) return id;
  if (id.startsWith('pitch')) return id.replace('pitch', 'pitch-');
  return `pitch-${id}`;
};

class AllocationImportExportService {
  // =============================================================================
  // EXPORT FUNCTIONS
  // =============================================================================

  /**
   * Export allocations to CSV format
   * UPDATED: Now includes facilityId and facilityName
   */
  exportToCSV(clubId, facilityId, pitchId, date, allocations, pitchName = '', facilityName = '') {
    console.log('📤 Exporting to CSV:', { clubId, facilityId, pitchId, date, allocationsCount: Object.keys(allocations).length });
    
    // CSV Headers - UPDATED to include facility columns
    const headers = [
      'Date',
      'Facility ID',
      'Facility Name',
      'Pitch',
      'Pitch Name',
      'Team',
      'Team ID',
      'Allocation Type',
      'Start Time',
      'End Time',
      'Duration (minutes)',
      'Sections',
      'Team Color',
      'Notes',
      'Booking ID',
      'Created At'
    ];

    // Convert allocations to CSV rows
    const rows = Object.entries(allocations).map(([id, allocation]) => {
      return [
        allocation.date || date,
        allocation.facilityId || facilityId || '',
        allocation.facilityName || facilityName || '',
        allocation.pitchId || allocation.pitch || pitchId,
        allocation.pitchName || pitchName || pitchId,
        allocation.teamName || allocation.team || '',
        allocation.teamId || '',
        allocation.allocationType || allocation.type || '',
        allocation.startTime || '',
        allocation.endTime || '',
        allocation.duration || '',
        Array.isArray(allocation.sections) ? allocation.sections.join('+') : (allocation.section || ''),
        allocation.color || allocation.colour || '',
        allocation.notes || '',
        allocation.bookingId || '',
        allocation.created || allocation.createdAt || ''
      ];
    });

    // Combine headers and rows
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n');

    console.log('✅ CSV generated:', csvContent.split('\n').length - 1, 'rows');
    return csvContent;
  }

  /**
   * Export allocations to JSON format
   * UPDATED: Now includes facilityId and facilityName
   */
  exportToJSON(clubId, facilityId, pitchId, date, allocations, pitchName = '', facilityName = '') {
    console.log('📤 Exporting to JSON:', { clubId, facilityId, pitchId, date, allocationsCount: Object.keys(allocations).length });
    
    const exportData = {
      exportDate: new Date().toISOString(),
      exportVersion: '2.0', // Version bump for facility support
      clubId,
      facilityId,
      facilityName,
      pitchId,
      pitchName,
      date,
      allocationsCount: Object.keys(allocations).length,
      allocations: Object.entries(allocations).map(([id, allocation]) => ({
        id,
        facilityId: allocation.facilityId || facilityId,
        facilityName: allocation.facilityName || facilityName,
        ...allocation
      }))
    };

    console.log('✅ JSON generated:', exportData.allocationsCount, 'allocations');
    return JSON.stringify(exportData, null, 2);
  }

  /**
   * Download allocations as a file
   */
  downloadFile(content, filename, mimeType) {
    console.log('💾 Downloading file:', filename);
    
    const blob = new Blob([content], { type: mimeType });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    console.log('✅ Download initiated');
  }

  /**
   * Export allocations for a specific facility, pitch and date
   * UPDATED: Now requires facilityId
   */
  async exportAllocations(clubId, facilityId, pitchId, date, allocations, format = 'csv', pitchName = '', facilityName = '') {
    try {
      console.log('🚀 Starting export:', { clubId, facilityId, pitchId, date, format, allocationsCount: Object.keys(allocations).length });
      
      // Generate filename with facility info
      const timestamp = new Date().toISOString().split('T')[0];
      const normalizedPitchId = pitchId.replace('pitch-', '').replace('pitch', '');
      const facilityPart = facilityId ? `_${facilityId}` : '';
      const filename = `allocations${facilityPart}_pitch${normalizedPitchId}_${date}_${timestamp}.${format}`;
      
      let content, mimeType;
      
      if (format === 'csv') {
        content = this.exportToCSV(clubId, facilityId, pitchId, date, allocations, pitchName, facilityName);
        mimeType = 'text/csv;charset=utf-8;';
      } else if (format === 'json') {
        content = this.exportToJSON(clubId, facilityId, pitchId, date, allocations, pitchName, facilityName);
        mimeType = 'application/json;charset=utf-8;';
      } else {
        throw new Error(`Unsupported export format: ${format}`);
      }
      
      this.downloadFile(content, filename, mimeType);
      
      console.log('✅ Export completed successfully');
      return { success: true, filename };
    } catch (error) {
      console.error('❌ Export failed:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Export all allocations for a facility on a specific date
   * UPDATED: Now facility-aware
   */
  async exportFacilityAllocationsForDate(clubId, facilityId, date, facilityName = '', format = 'csv') {
    try {
      console.log('🚀 Starting facility export:', { clubId, facilityId, date, format });
      
      // Load allocations for the facility
      const allocations = await allocationService.getAllocationsForFacilityAndDate(clubId, facilityId, date);
      
      if (allocations.length === 0) {
        console.log('ℹ️ No allocations found for facility on this date');
        return { success: true, message: 'No allocations to export' };
      }
      
      // Convert to object format for export functions
      const allocationsObj = {};
      allocations.forEach(allocation => {
        allocationsObj[allocation.id] = allocation;
      });
      
      // Generate filename
      const timestamp = new Date().toISOString().split('T')[0];
      const filename = `allocations_${facilityId}_${date}_${timestamp}.${format}`;
      
      let content, mimeType;
      
      if (format === 'csv') {
        content = this.exportToCSV(clubId, facilityId, 'all', date, allocationsObj, 'All Pitches', facilityName);
        mimeType = 'text/csv;charset=utf-8;';
      } else if (format === 'json') {
        content = this.exportToJSON(clubId, facilityId, 'all', date, allocationsObj, 'All Pitches', facilityName);
        mimeType = 'application/json;charset=utf-8;';
      } else {
        throw new Error(`Unsupported export format: ${format}`);
      }
      
      this.downloadFile(content, filename, mimeType);
      
      console.log('✅ Facility export completed:', allocations.length, 'allocations');
      return { success: true, filename, count: allocations.length };
    } catch (error) {
      console.error('❌ Facility export failed:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Export all allocations for all facilities on a specific date
   * NEW: Export everything for a date across all facilities
   */
  async exportAllFacilitiesForDate(clubId, facilities, date, format = 'csv') {
    try {
      console.log('🚀 Starting bulk export for all facilities:', { clubId, facilityCount: facilities.length, date, format });
      
      const allAllocations = [];
      
      for (const facility of facilities) {
        const allocations = await allocationService.getAllocationsForFacilityAndDate(
          clubId,
          facility.id,
          date
        );
        
        allocations.forEach(allocation => {
          allAllocations.push({
            ...allocation,
            facilityId: facility.id,
            facilityName: facility.name
          });
        });
      }
      
      if (allAllocations.length === 0) {
        console.log('ℹ️ No allocations found for any facility on this date');
        return { success: true, message: 'No allocations to export' };
      }
      
      // Convert to object format
      const allocationsObj = {};
      allAllocations.forEach(allocation => {
        allocationsObj[allocation.id] = allocation;
      });
      
      // Generate filename
      const timestamp = new Date().toISOString().split('T')[0];
      const filename = `allocations_all_facilities_${date}_${timestamp}.${format}`;
      
      let content, mimeType;
      
      if (format === 'csv') {
        content = this.exportToCSV(clubId, 'all', 'all', date, allocationsObj, 'All Pitches', 'All Facilities');
        mimeType = 'text/csv;charset=utf-8;';
      } else if (format === 'json') {
        content = this.exportToJSON(clubId, 'all', 'all', date, allocationsObj, 'All Pitches', 'All Facilities');
        mimeType = 'application/json;charset=utf-8;';
      } else {
        throw new Error(`Unsupported export format: ${format}`);
      }
      
      this.downloadFile(content, filename, mimeType);
      
      console.log('✅ Bulk export completed:', allAllocations.length, 'allocations from', facilities.length, 'facilities');
      return { success: true, filename, count: allAllocations.length };
    } catch (error) {
      console.error('❌ Bulk export failed:', error);
      return { success: false, error: error.message };
    }
  }

  // =============================================================================
  // IMPORT FUNCTIONS
  // =============================================================================

  /**
   * Parse CSV content to array of objects
   */
  parseCSV(csvContent) {
    console.log('📊 Parsing CSV content...');
    
    const lines = csvContent.split('\n').filter(line => line.trim());
    if (lines.length < 2) {
      throw new Error('CSV file is empty or has no data rows');
    }
    
    // Parse headers
    const headers = lines[0].split(',').map(h => h.replace(/"/g, '').trim());
    console.log('📋 CSV headers:', headers);
    
    // Parse data rows
    const data = [];
    for (let i = 1; i < lines.length; i++) {
      const values = this.parseCSVLine(lines[i]);
      if (values.length === headers.length) {
        const row = {};
        headers.forEach((header, index) => {
          row[header] = values[index];
        });
        data.push(row);
      }
    }
    
    console.log('✅ Parsed', data.length, 'rows from CSV');
    return data;
  }

  /**
   * Parse a single CSV line, handling quoted values
   */
  parseCSVLine(line) {
    const values = [];
    let current = '';
    let inQuotes = false;
    
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      
      if (char === '"') {
        if (inQuotes && line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === ',' && !inQuotes) {
        values.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    
    values.push(current.trim());
    return values;
  }

  /**
   * Convert parsed allocations to Firestore format
   * UPDATED: Now includes facilityId
   */
  convertToFirestoreFormat(parsedAllocations, clubId, defaultFacilityId = null) {
    console.log('🔄 Converting', parsedAllocations.length, 'allocations to Firestore format...');
    
    return parsedAllocations.map(allocation => {
      // Parse sections
      let sections = [];
      if (allocation.Sections || allocation.sections) {
        const sectionsData = allocation.Sections || allocation.sections;
        if (Array.isArray(sectionsData)) {
          sections = sectionsData;
        } else {
          sections = sectionsData.split(/[+,]/).map(s => s.trim()).filter(s => s);
        }
      }
      
      // Parse duration
      const duration = parseInt(allocation['Duration (minutes)'] || allocation.duration || '30', 10);
      
      // Get facilityId from allocation or use default
      const facilityId = allocation['Facility ID'] || allocation.facilityId || defaultFacilityId;
      
      if (!facilityId) {
        console.warn('⚠️ No facilityId found for allocation, using default');
      }
      
      return {
        facilityId: facilityId || defaultFacilityId,
        facilityName: allocation['Facility Name'] || allocation.facilityName || '',
        date: allocation.Date || allocation.date,
        pitchId: normalizePitchId(allocation.Pitch || allocation.pitchId || allocation.pitch),
        pitchName: allocation['Pitch Name'] || allocation.pitchName || '',
        teamName: allocation.Team || allocation.teamName || allocation.team,
        teamId: allocation['Team ID'] || allocation.teamId || allocation.Team || allocation.team,
        allocationType: allocation['Allocation Type'] || allocation.allocationType || allocation.type || 'training',
        startTime: allocation['Start Time'] || allocation.startTime,
        endTime: allocation['End Time'] || allocation.endTime,
        duration,
        sections,
        color: allocation['Team Color'] || allocation.color || allocation.colour || '#cccccc',
        notes: allocation.Notes || allocation.notes || '',
        bookingId: allocation['Booking ID'] || allocation.bookingId || null,
        clubId
      };
    });
  }

  /**
   * Import allocations from CSV content
   * UPDATED: Now requires facilityId
   */
  async importFromCSV(csvContent, clubId, defaultFacilityId, replaceExisting = false) {
    try {
      console.log('🚀 Starting CSV import for club:', clubId, 'facility:', defaultFacilityId);
      
      if (!defaultFacilityId) {
        throw new Error('facilityId is required for import');
      }
      
      // Parse CSV
      const parsedAllocations = this.parseCSV(csvContent);
      
      // Convert to Firestore format
      const allocations = this.convertToFirestoreFormat(parsedAllocations, clubId, defaultFacilityId);
      
      // Import allocations
      return await this.importAllocations(allocations, clubId, replaceExisting);
    } catch (error) {
      console.error('❌ CSV import failed:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Import allocations from JSON content
   * UPDATED: Now requires facilityId
   */
  async importFromJSON(jsonContent, clubId, defaultFacilityId, replaceExisting = false) {
    try {
      console.log('🚀 Starting JSON import for club:', clubId, 'facility:', defaultFacilityId);
      
      if (!defaultFacilityId) {
        throw new Error('facilityId is required for import');
      }
      
      // Parse JSON
      const data = JSON.parse(jsonContent);
      
      // Validate JSON structure
      if (!data.allocations || !Array.isArray(data.allocations)) {
        throw new Error('Invalid JSON format: missing or invalid allocations array');
      }
      
      console.log('📊 Found', data.allocations.length, 'allocations in JSON');
      
      // Use facilityId from JSON if available, otherwise use default
      const facilityId = data.facilityId || defaultFacilityId;
      
      // Convert to Firestore format
      const allocations = this.convertToFirestoreFormat(data.allocations, clubId, facilityId);
      
      // Import allocations
      return await this.importAllocations(allocations, clubId, replaceExisting);
    } catch (error) {
      console.error('❌ JSON import failed:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Import allocations into Firestore
   * UPDATED: Uses new flattened allocation path
   */
  async importAllocations(allocations, clubId, replaceExisting = false) {
    try {
      console.log('📥 Importing', allocations.length, 'allocations...');
      console.log('🔄 Replace existing:', replaceExisting);
      
      let successCount = 0;
      let skipCount = 0;
      let errorCount = 0;
      const errors = [];
      
      // Group allocations by date and facility for efficient deletion
      if (replaceExisting) {
        const groupedForDeletion = {};
        allocations.forEach(allocation => {
          const key = `${allocation.date}_${allocation.facilityId}_${allocation.pitchId}`;
          if (!groupedForDeletion[key]) {
            groupedForDeletion[key] = { 
              date: allocation.date, 
              facilityId: allocation.facilityId,
              pitchId: allocation.pitchId 
            };
          }
        });
        
        // Delete existing allocations for these dates/facilities/pitches
        for (const key of Object.keys(groupedForDeletion)) {
          const { date, facilityId, pitchId } = groupedForDeletion[key];
          console.log(`🗑️ Deleting existing allocations for facility ${facilityId}, ${pitchId} on ${date}...`);
          
          // Use pitch-based subcollection
          const allocationsRef = collection(db, buildFirestorePath.allocationsCollection(clubId, pitchId));
          const q = query(
            allocationsRef, 
            where('facilityId', '==', facilityId),
            where('date', '==', date)
          );
          const snapshot = await getDocs(q);
          
          const batch = writeBatch(db);
          snapshot.docs.forEach(doc => {
            batch.delete(doc.ref);
          });
          
          await batch.commit();
          console.log(`✅ Deleted ${snapshot.docs.length} existing allocations`);
        }
      }
      
      // Import allocations
      for (const allocation of allocations) {
        try {
          // Validate facilityId
          if (!allocation.facilityId) {
            throw new Error('Missing facilityId');
          }
          
          // Prepare allocation data
          const allocationData = {
            facilityId: allocation.facilityId,
            pitchId: allocation.pitchId,
            pitchName: allocation.pitchName || allocation.pitchId,
            sections: allocation.sections,
            date: allocation.date,
            startTime: allocation.startTime,
            endTime: allocation.endTime,
            duration: allocation.duration,
            teamId: allocation.teamId,
            teamName: allocation.teamName,
            allocationType: allocation.allocationType,
            notes: allocation.notes || '',
            isRecurring: false,
            recurrencePattern: null
          };
          
          // Create allocation using the service
          const result = await allocationService.createAllocation(clubId, allocationData);
          
          if (result.success) {
            successCount++;
          } else {
            if (result.error.includes('conflict')) {
              skipCount++;
              console.log(`⏭️ Skipped (conflict): ${allocation.teamName} on ${allocation.date} at ${allocation.startTime}`);
            } else {
              errorCount++;
              errors.push({
                allocation,
                error: result.error
              });
              console.error(`❌ Error: ${result.error}`);
            }
          }
        } catch (error) {
          errorCount++;
          errors.push({
            allocation,
            error: error.message
          });
          console.error('❌ Import error for allocation:', allocation, error);
        }
      }
      
      console.log('📊 Import summary:', { successCount, skipCount, errorCount });
      
      return {
        success: true,
        imported: successCount,
        skipped: skipCount,
        errors: errorCount,
        errorDetails: errors
      };
    } catch (error) {
      console.error('❌ Import failed:', error);
      return { success: false, error: error.message };
    }
  }

  // =============================================================================
  // LEGACY SUPPORT
  // =============================================================================

  /**
   * Export allocations using legacy path structure
   * DEPRECATED: Use exportAllocations with facilityId instead
   */
  async exportLegacyAllocations(clubId, pitchId, date, format = 'csv', pitchName = '') {
    console.warn('⚠️ Using legacy export - consider migrating to facility-aware export');
    
    try {
      // Try to get allocations from legacy path
      const allocationsRef = collection(db, 'clubs', clubId, 'pitches', pitchId, 'allocations');
      const q = query(allocationsRef, where('date', '==', date));
      const snapshot = await getDocs(q);
      
      const allocations = {};
      snapshot.docs.forEach(doc => {
        allocations[doc.id] = doc.data();
      });
      
      return await this.exportAllocations(clubId, null, pitchId, date, allocations, format, pitchName, '');
    } catch (error) {
      console.error('❌ Legacy export failed:', error);
      return { success: false, error: error.message };
    }
  }
}

// Create and export singleton instance
const allocationImportExportService = new AllocationImportExportService();
export default allocationImportExportService;
